import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TablePagination,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { SnackBarMessage, SideBar, LG_WIDTH, slugify } from '@eposnow/ui-core';
import { UserContext } from '../../context/UserContext';
import useCountries from '../../helpers/useCountries';
import { UIContext } from '../../context/UIContext';
import { ADDRESS_DRAWER_WIDTH } from '../../constants';
import ServerAlertMessage from '../../components/ServerAlertMessage';
import ErrorLoadingData from '../../components/ErrorLoadingData';
import CardButton from '../../components/CardButton';
import CreateEditAddressCard from './CreateEditAddressCard';
import ActionsContainer from '../../components/ActionsContainer';
import InfoAddressCard from './InfoAddressCard';
import { StepContext } from '../../context/StepContext';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import { PageMetadata } from '../../components/datagridComponents/paginationHelpers';
import { LocationV1 } from '../../types';
import { FilterContext } from '../../context/FilterContext';
import useFiltersFetchedData from '../../helperHooks/useFiltersFetchedData';
import usePageTitle from '../../helperHooks/usePageTitle';

const LocationsAllAddressesList = () => {
  const theme = useTheme();
  const dimensionsTheme = useTheme();
  const { addressId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { apiFetch } = useContext(ApiContext);
  const { locale } = useContext(UserContext);
  const { goToPreviousStep, goToNextStep } = useContext(StepContext);
  const { isMobile, animationsDisabled, iOS } = useContext(UIContext);
  const {
    countries,
    fetchData: fetchCountries,
    isLoading: isLoadingCountries,
    showAlertMessage,
  } = useCountries();
  const [isCreatingAddress, setIsCreatingAddress] = useState(false);
  const [addressBeingEdited, setAddressBeingEdited] = useState<LocationV1 | undefined>(undefined);
  const [successAddressMessage, setSuccessAddressMessage] = useState<string>('');
  const [errorAddressMessage, setErrorAddressMessage] = useState<string>('');
  const isLargeDeviceSwipeable = useMediaQuery(
    dimensionsTheme.breakpoints.up(LG_WIDTH + ADDRESS_DRAWER_WIDTH)
  );
  const messageRef = useRef<null | HTMLElement>(null);
  const { paginationModel, setPaginationModel } = useContext(FilterContext);

  usePageTitle('nav.locations');

  const resetCurrentAddress = () => {
    if (isCreatingAddress) {
      setIsCreatingAddress(false);
    } else {
      setAddressBeingEdited(undefined);
    }
    setErrorAddressMessage('');
  };

  const resetAndNavigate = () => {
    resetCurrentAddress();
    navigate('');
  };

  useEffect(() => {
    if (showAlertMessage && messageRef.current)
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showAlertMessage]);

  const {
    fetchData: fetchAddresses,
    data: addresses,
    isLoading,
    isError,
    error,
  } = useFiltersFetchedData<{ data: LocationV1[]; metadata: PageMetadata }>(
    'addresses',
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/location`,
    'addresses'
  );

  const { mutate: deleteLocation } = useMutation(
    (id: number) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/location/${id}`,
        {},
        'DELETE'
      ),
    {
      onSuccess() {
        setSuccessAddressMessage(t('screens.locations2.deleted'));
        fetchAddresses();
      },
      onError(errorResponse: ErrorObject) {
        setErrorAddressMessage(t('screens.locations2.errorDeleting'));

        if (errorResponse?.code === 401) return;
        Sentry.captureException(new Error('Error deleting address'), {
          extra: { error: JSON.stringify(errorResponse) },
        });
      },
    }
  );

  const unauthorized = (error as any)?.code === 401;

  useEffect(() => {
    if (addressId && addresses) {
      resetCurrentAddress();
      if (addressId === '0') {
        setIsCreatingAddress(true);
      } else {
        const address = addresses?.data.find(
          (elementAddress) => `${elementAddress.id}` === addressId
        );
        if (address) {
          setAddressBeingEdited(address);
        }
      }
    } else if (isCreatingAddress || addressBeingEdited) {
      resetCurrentAddress();
    }
  }, [addressId, addresses]);

  return (
    <Box marginBottom="5em">
      {isLoading || unauthorized ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '96px',
          }}
        >
          <CircularProgress
            aria-label="Loading Data"
            aria-live="polite"
            aria-busy={isLoading || unauthorized}
            data-testid="loading-icon"
            sx={{ color: 'text.secondary' }}
          />
        </Box>
      ) : (
        <>
          {showAlertMessage && (
            <ServerAlertMessage
              message={t(`errors.errorLoadingCountries`)}
              isLoading={isLoadingCountries}
              refetch={fetchCountries}
            />
          )}
          <SnackBarMessage
            message={successAddressMessage}
            setMessage={setSuccessAddressMessage}
            isMobile={isMobile}
            locale={locale}
            theme={theme}
          />
          {isError && <ErrorLoadingData action={fetchAddresses} />}
          {!isError && (
            <>
              <Grid container spacing={{ xs: 1, sm: 3 }}>
                <Grid item xs={12} sm={6} lg={4}>
                  <CardButton
                    icon={<AddOutlinedIcon />}
                    action={() => addressId !== '0' && navigate('0')}
                    styles={{
                      ...(!isMobile &&
                        addresses?.data.length === 0 && {
                          height: '280px',
                        }),
                    }}
                  >
                    <Typography variant="h2">{t('screens.locations2.addNewAddress')}</Typography>
                  </CardButton>
                </Grid>
                {addresses?.data?.map((address: LocationV1) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    key={`address-${slugify(`${address.id}`)}`}
                    sx={{ display: 'flex' }}
                  >
                    <InfoAddressCard
                      countries={countries}
                      address={address}
                      clickEditButton={() => {
                        if (addressId !== `${address.id}`) navigate(`${address.id}`);
                      }}
                      clickDeleteButton={deleteLocation}
                    />
                  </Grid>
                ))}
              </Grid>
              {addresses?.metadata?.totalRecords > addresses?.metadata?.limit && (
                <TablePagination
                  component="div"
                  count={addresses?.metadata?.totalRecords}
                  page={addresses.metadata.page - 1}
                  onPageChange={(_event, page) => {
                    setPaginationModel({
                      page,
                      pageSize: paginationModel.pageSize,
                    });
                  }}
                  rowsPerPage={paginationModel.pageSize}
                  onRowsPerPageChange={(event) => {
                    setPaginationModel({
                      page: 0,
                      pageSize: parseInt(event.target.value, 10),
                    });
                  }}
                  rowsPerPageOptions={[2, 5, 8]}
                  labelRowsPerPage={t('screens.locations2.addressesPerPage')}
                  labelDisplayedRows={(paginationInfo) =>
                    t('screens.locations2.fromToAddresses', {
                      from: paginationInfo?.from,
                      to: paginationInfo.to,
                      count: paginationInfo.count,
                    })
                  }
                />
              )}
            </>
          )}
          {(isCreatingAddress || addressBeingEdited) && (
            <SideBar
              open={isCreatingAddress || addressBeingEdited != null}
              toggleDrawer={resetAndNavigate}
              showSwipeableDrawer={!isMobile && !isLargeDeviceSwipeable}
              width={{
                drawerWidth: !isMobile ? ADDRESS_DRAWER_WIDTH : undefined,
                closedDrawerWidth: 0,
              }}
              anchor="right"
              belowTopBar={!isMobile}
              disableSwipe={!isMobile}
              iOS={iOS}
              animationsDisabled={animationsDisabled}
              theme={theme}
            >
              <>
                {!isMobile && <Toolbar />}
                <CreateEditAddressCard
                  currentAddress={isCreatingAddress ? undefined : addressBeingEdited}
                  resetAddress={resetAndNavigate}
                  setSuccessAddressMessage={setSuccessAddressMessage}
                  errorAddressMessage={errorAddressMessage}
                  setErrorAddressMessage={setErrorAddressMessage}
                  mode={isCreatingAddress ? 'create' : 'edit'}
                />
              </>
            </SideBar>
          )}
        </>
      )}
      <ActionsContainer>
        <Button
          variant="outlined"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={goToPreviousStep}
        >
          {t('actions.previous')}
        </Button>

        <Button
          variant="contained"
          sx={{
            margin: '0px',
            flexGrow: '0',
            height: '48px',
          }}
          onClick={goToNextStep}
        >
          {t('actions.next')}
        </Button>
      </ActionsContainer>
    </Box>
  );
};

export default LocationsAllAddressesList;
